import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Zap, Building2, Landmark, Trophy, Moon, Cpu, Truck, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const useCases = [
  { label: "Corporate Events", icon: Building2 },
  { label: "Government", icon: Landmark },
  { label: "Sports Events", icon: Trophy },
  { label: "Hajj & Umrah", icon: Moon },
  { label: "Smart Cities", icon: Cpu },
];

const fleetVehicles = [
  { name: "iTank", image: "/products/itank/itank-hero.png", type: "3-Wheel Patrol" },
  { name: "iTango", image: "/products/itango/itango-angle-1.png", type: "Luxury Scooter" },
  { name: "iLark", image: "/products/ilark/ilark-black-angle.png", type: "City Commuter" },
];

const stats = [
  { value: "500+", label: "Vehicles Deployed" },
  { value: "50+", label: "Events Served" },
  { value: "100%", label: "Electric Fleet" },
];

const LemoBanner = () => {
  return (
    <section className="py-20 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-eco-green/5 to-background" />
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-eco-green/5 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-teal-bright/5 blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Main content grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Left: Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-eco-green" />
              <span className="text-sm font-bold text-eco-green uppercase tracking-wider">LEMO Rentals</span>
            </div>

            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
              Rent a Fleet for{" "}
              <span className="bg-gradient-to-r from-eco-green via-teal-bright to-eco-green-light bg-clip-text text-transparent">
                Your Next Event
              </span>
            </h2>

            <p className="text-lg text-muted-foreground mb-8 max-w-lg leading-relaxed">
              Electric vehicles delivered, managed, and collected. From corporate conferences to government events — LEMO handles your mobility end-to-end.
            </p>

            {/* Use case tags */}
            <div className="flex flex-wrap gap-2 mb-8">
              {useCases.map((useCase, i) => (
                <motion.div
                  key={useCase.label}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 * i, duration: 0.3 }}
                >
                  <Badge
                    variant="outline"
                    className="px-3 py-1.5 text-sm border-eco-green/30 text-foreground bg-eco-green/10 hover:bg-eco-green/20 transition-colors gap-1.5"
                  >
                    <useCase.icon className="w-3.5 h-3.5 text-eco-green" />
                    {useCase.label}
                  </Badge>
                </motion.div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4">
              <Button
                size="xl"
                className="bg-gradient-to-r from-eco-green to-teal-bright text-white shadow-lg shadow-eco-green/25 hover:shadow-xl hover:shadow-eco-green/30 hover:scale-105 transition-all duration-300"
                asChild
              >
                <Link to="/lemo">
                  Explore LEMO <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button variant="outline" size="xl" asChild className="border-eco-green/30 hover:bg-eco-green/10">
                <Link to="/lemo#booking">Request a Quote</Link>
              </Button>
            </div>
          </motion.div>

          {/* Right: Fleet showcase */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 gap-4"
          >
            {fleetVehicles.map((vehicle, i) => (
              <motion.div
                key={vehicle.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.15 * i + 0.3, duration: 0.4 }}
              >
                <Link
                  to="/lemo"
                  className="group flex items-center gap-4 p-4 rounded-2xl bg-card/60 border border-border/50 hover:border-eco-green/30 hover:bg-eco-green/5 transition-all duration-300"
                >
                  <div className="w-24 h-24 rounded-xl bg-secondary/50 overflow-hidden shrink-0 flex items-center justify-center">
                    <img
                      src={vehicle.image}
                      alt={vehicle.name}
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-foreground text-lg">{vehicle.name}</h3>
                    <p className="text-sm text-muted-foreground">{vehicle.type}</p>
                  </div>
                  <Badge className="bg-eco-green/15 text-eco-green border-eco-green/30 text-xs shrink-0">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    For Rent
                  </Badge>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="grid grid-cols-3 gap-6 max-w-2xl mx-auto"
        >
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-eco-green mb-1">{stat.value}</div>
              <div className="text-xs md:text-sm text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default LemoBanner;
